// Machine Archive — /pro-d

const CONTENT = {
  name: 'Aron Grünwald',
  email: 'kontakt@aron-systems.de',
  github: 'https://github.com/knusperkrone',
  gitlab: 'https://gitlab.com/knukro',
  linkedin: 'https://www.linkedin.com/in/Aron-m',
  photoPath: 'photo.jpg'
};

const TRANSLATIONS = {
  "en": {
    "meta": {
      "title": "Aron Grünwald | Freelance Software Architect",
      "description": "Berlin-based software architect: requirements analysis, system design and implementation. Experience in finance, insurance, distributed systems and AI integration."
    },
    "nav": {
      "skip": "Skip to projects",
      "work": "Projects",
      "capabilities": "Expertise",
      "downloads": "CV & thesis",
      "contact": "Contact",
      "backToTop": "Return to page start",
      "mainNavAria": "Main navigation",
      "langAria": "Choose language"
    },
    "hero": {
      "sectionAria": "About Aron Grünwald",
      "label": "Freelance Software Architect & Engineer",
      "pitch": "I clarify requirements, design system architectures and implement critical components.<br>I help teams evolve existing systems and integrate AI.",
      "meta": "Berlin · Software engineering since 2017 · Experience in finance & insurance",
      "cta": "Discuss your project",
      "ctaSubject": "Project enquiry",
      "githubAria": "View my GitHub profile",
      "gitlabAria": "View my GitLab profile",
      "linkedinAria": "View my LinkedIn profile"
    },
    "work": {
      "project1": {
        "label": "FLEXPERTO — AI & PLATFORM ARCHITECTURE / FINANCE & INSURANCE — 2022–2025",
        "title": "Speech-to-text from idea to product",
        "p1": "At Flexperto, I worked with the CTO to take a speech-to-text service from the initial idea through an MVP to a product. I designed the processing architecture and implemented the pipeline from WebRTC audio capture through voice activity detection and messaging to transcription.",
        "p2": "Using short audio segments and batching on an NVIDIA H100, the pipeline achieved 150% higher throughput than the faster-whisper baseline in a test with a 50-minute recording. Word error rate was unchanged in that test, with approximately 20% higher RAM usage. Production processing used batches; I demonstrated live transcription separately.",
        "p3": "Across the platform, I designed OpenAPI interfaces, aligned solutions with business stakeholders and developers, and used UML to diagnose failures spanning services. One case involved a lost wakeup across four services, which I investigated and fixed. I also coordinated developers and shared knowledge through technical talks.",
        "alt": "Flexperto transcription interface; the production pipeline described here used batching.",
        "val1": "+150%",
        "metric1": "Transcription throughput in the 50-minute audio comparison test",
        "val2": "Same WER",
        "metric2": "Word error rate in the same comparison test",
        "val3": "Idea → product",
        "metric3": "Shared responsibility with the CTO",
        "stack": "WEBRTC · SILERO VAD · RABBITMQ · FASTER-WHISPER · NVIDIA H100"
      },
      "project2": {
        "label": "NAROIQ — DISTRIBUTED SYSTEMS / FINANCE — 2025",
        "title": "Market-data architecture with scheduler failover",
        "p1": "At NaroIQ, I shared responsibility for the overall architecture of a distributed market-data platform. I independently designed and implemented its Raft-based scheduler for ETF calculations. It replaced inflexible cron processing and coordinated which instance triggered processing events.",
        "p2": "Coordinating multiple scheduler instances supported availability: another instance could take over event triggering when the active instance failed. With the Head of Engineering, I designed RabbitMQ topic routing, separate queues and specialised workers. We agreed on recovery and retry behaviour as a team.",
        "p3": "I documented the flows in UML, built AWS infrastructure with Terraform and deployed VPN-isolated services. Grafana dashboards provided visibility into processing status and costs per financial instrument. I also administered Keycloak and integrated authentication into the services.",
        "alt": "NaroIQ scheduler dashboard with data-feed processing and infrastructure status.",
        "val1": "Failover",
        "metric1": "Take over event triggering when the active scheduler instance fails",
        "val2": "System architecture",
        "metric2": "Shared responsibility for the distributed market-data platform",
        "val3": "Cost visibility",
        "metric3": "Track processing costs per financial instrument",
        "stack": "RAFT · RABBITMQ · UML · AWS · TERRAFORM · GRAFANA · KEYCLOAK"
      },
      "project3": {
        "label": "HOLZ DISSELNMEYER — SYSTEM INTEGRATION & PRODUCT OWNERSHIP — 2023–PRESENT",
        "title": "Turning business rules into an integrated system",
        "p1": "For Holz Disselnmeyer, I own the architecture, implementation and operations of a platform connecting Shopware 6, Business Central and a custom PIM. Working with management, business teams and external suppliers, I clarify requirements and translate pricing, shipping and order workflows into system boundaries, data models and interfaces.",
        "p2": "Local pricing models and incremental ERP synchronisation avoid a direct ERP dependency for each price lookup. Orders are transferred asynchronously with retries and persisted processing status. Shipping rules account for different units, dimensions and delivery options.",
        "p3": "I built the FastAPI and React PIM for more than 15,000 catalogue items, including variants. Bulk editing, AI-assisted grouping and image processing support catalogue maintenance. I tailored the interface to the content and SEO team. Tests cover releases and migrations; Grafana and Prometheus support monitoring and incident diagnosis.",
        "alt": "Commerce interface showing catalogue, pricing and order-management functions.",
        "val1": "15,000+",
        "metric1": "Catalogue items, including variants, managed in the PIM",
        "val2": "Full ownership",
        "metric2": "Requirements, architecture, implementation and operations",
        "val3": "Since 2025",
        "metric3": "Shop serving customers",
        "stack": "FASTAPI · REACT · BUSINESS CENTRAL · SHOPWARE 6 · REDIS · TERRAFORM"
      },
      "expand": {
        "readMore": "Read project details",
        "showLess": "Collapse project details"
      }
    },
    "capabilities": {
      "label": "Expertise",
      "title": "Clear requirements. Informed technical decisions.",
      "subtitle": "I develop solutions with business stakeholders and engineering teams, explain the trade-offs and document the architecture using UML and arc42. I support implementation with tests, monitoring and production diagnosis.",
      "nodes": {
        "arch": {
          "name": "Requirements & architecture",
          "detail": "Abstract business rules; define system boundaries and solution options"
        },
        "python": {
          "name": "Backend & microservices",
          "detail": "Critical components in Python and Java; APIs with explicit contracts"
        },
        "grafana": {
          "name": "Monitoring & diagnosis",
          "detail": "Root-cause analysis across services; visibility into processing and costs"
        },
        "rust": {
          "name": "System integration",
          "detail": "Connect existing applications; design asynchronous flows and failure paths"
        },
        "infra": {
          "name": "Cloud & infrastructure",
          "detail": "AWS, Terraform and Kubernetes; deployment and operations"
        },
        "data": {
          "name": "Domain & data modelling",
          "detail": "Translate business rules into data models and traceable state"
        },
        "middleware": {
          "name": "Distributed systems",
          "detail": "Event-driven processing, availability and recovery"
        },
        "ai": {
          "name": "AI integration & evaluation",
          "detail": "RAG, local models and MCP; quality checks using evaluation datasets"
        },
        "auth": {
          "name": "Security & tenant isolation",
          "detail": "Design tenant isolation; integrate authentication and mTLS"
        }
      }
    },
    "downloads": {
      "label": "CV & thesis",
      "cv": {
        "title": "CV",
        "meta": "PDF",
        "desc": "Project responsibilities and career history: architecture, development and operations, with experience in finance and insurance.",
        "cta": "Download my CV",
        "file": "cv-en.pdf",
        "filename": "Aron_Gruenwald_CV_EN.pdf"
      },
      "thesis": {
        "title": "Master’s thesis — Kubernetes elasticity and resilience",
        "meta": "PDF",
        "desc": "Academic research into Kubernetes resource utilisation, using Helm, ArgoCD and Istio.",
        "cta": "Read my Kubernetes thesis",
        "file": "master-thesis.pdf",
        "filename": "master-thesis.pdf"
      }
    },
    "contact": {
      "label": "What does your system need to do next?",
      "intro": "Evolving an existing platform, working through architecture decisions or integrating AI features? Send me a short outline. We can discuss your goals, current system and how to work together.",
      "emailLabel": "Get in touch about a project",
      "terminalTitle": "aron@aron-systems ~ portfolio",
      "terminalHint": "Enter help, whoami, stack, projects or contact.",
      "terminalPlaceholder": "Enter a command (try help)",
      "terminalAria": "Portfolio command",
      "terminalAriaLabel": "Portfolio terminal",
      "status": "Freelance collaboration · Scope and availability discussed individually",
      "commands": {
        "help": [
          "Portfolio commands:",
          "  help      -> command list",
          "  whoami    -> role and responsibilities",
          "  stack     -> technologies used in projects",
          "  projects  -> selected projects",
          "  uptime    -> career timeline",
          "  contact   -> email and profiles",
          "  clear     -> clear output"
        ],
        "whoami": [
          "Aron Grünwald",
          "Freelance Software Architect & Engineer.",
          "Requirements analysis, system architecture and hands-on implementation.",
          "Experience in finance and insurance, distributed systems and AI integration.",
          "Working directly with business stakeholders, CTOs and engineering teams."
        ],
        "stack": [
          "development:     Python, Java, TypeScript, PHP, FastAPI, Quarkus, React",
          "infrastructure:  AWS, Kubernetes, Terraform, Docker",
          "messaging/data:  RabbitMQ, PostgreSQL, MySQL, Redis",
          "ai:              RAG, local models, evaluation datasets, MCP, faster-whisper, vLLM",
          "integration:     OpenAPI, OData, Business Central, Shopware 6",
          "architecture:    UML, arc42, domain models, event-driven systems",
          "quality:         Unit, integration and smoke tests; Grafana, Prometheus, ELK"
        ],
        "projects": [
          "Flexperto: speech-to-text from idea to product; platform architecture.",
          "NaroIQ: shared market-data architecture; implemented scheduler failover.",
          "Holz Disselnmeyer: requirements, architecture and operations; PIM for 15,000+ items."
        ],
        "uptime": [
          "Software engineering since June 2017.",
          "From requirements and architecture through implementation to operations."
        ],
        "contact": [
          "email:     kontakt@aron-systems.de",
          "github:    github.com/knusperkrone",
          "gitlab:    gitlab.com/knukro",
          "linkedin:  linkedin.com/in/Aron-m"
        ]
      }
    },
    "footer": {
      "text": "© 2026 Aron Grünwald — Freelance Software Architect, Berlin"
    }
  },
  "de": {
    "meta": {
      "title": "Aron Grünwald | Freiberuflicher Softwarearchitekt",
      "description": "Softwarearchitekt aus Berlin: Anforderungsanalyse, Systemarchitektur und Umsetzung. Erfahrung in Finance und Insurance, verteilten Systemen und KI-Integration."
    },
    "nav": {
      "skip": "Direkt zu den Projekten",
      "work": "Projekte",
      "capabilities": "Expertise",
      "downloads": "Lebenslauf & Masterarbeit",
      "contact": "Kontakt",
      "backToTop": "Zum Seitenanfang",
      "mainNavAria": "Hauptnavigation",
      "langAria": "Sprache wählen"
    },
    "hero": {
      "sectionAria": "Über Aron Grünwald",
      "label": "Freiberuflicher Softwarearchitekt & Engineer",
      "pitch": "Ich kläre Anforderungen, entwerfe Systemarchitekturen und implementiere kritische Komponenten.<br>Für die Weiterentwicklung bestehender Systeme und die Integration von KI.",
      "meta": "Berlin · Softwareentwicklung seit 2017 · Erfahrung in Finance & Insurance",
      "cta": "Projekt besprechen",
      "ctaSubject": "Projektanfrage",
      "githubAria": "GitHub-Profil ansehen",
      "gitlabAria": "GitLab-Profil ansehen",
      "linkedinAria": "LinkedIn-Profil ansehen"
    },
    "work": {
      "project1": {
        "label": "FLEXPERTO — KI & PLATTFORMARCHITEKTUR / FINANCE & INSURANCE — 2022–2025",
        "title": "Speech-to-Text von der Idee bis zum Produkt",
        "p1": "Bei Flexperto verantwortete ich gemeinsam mit dem CTO einen Speech-to-Text-Service von der Idee über das MVP bis zum Produkt. Ich entwarf die Verarbeitungsarchitektur und implementierte die Pipeline von der WebRTC-Audioaufnahme über Sprachaktivitätserkennung und Messaging bis zur Transkription.",
        "p2": "Durch kurze Audiosegmente und Batching erzielte die Verarbeitung auf einer NVIDIA H100 im Test mit einer 50-minütigen Aufnahme 150 % mehr Durchsatz als die faster-whisper-Vergleichslösung. Die Wortfehlerrate blieb in diesem Test gleich, bei rund 20 % höherem RAM-Bedarf. Die produktive Verarbeitung lief im Batchbetrieb; Live-Transkription demonstrierte ich separat.",
        "p3": "Für die Plattform entwarf ich OpenAPI-Schnittstellen, stimmte Lösungen mit Fachseite und Entwicklungsteam ab und analysierte dienstübergreifende Fehler mit UML. Dazu gehörte ein verlorenes Wecksignal über vier Dienste hinweg, dessen Ursache ich untersuchte und behob. Technikvorträge und fachliche Koordination unterstützten den Wissenstransfer im Team.",
        "alt": "Flexperto-Transkriptionsoberfläche; die hier beschriebene produktive Pipeline nutzte Batching.",
        "val1": "+150 %",
        "metric1": "Transkriptionsdurchsatz im Vergleichstest mit 50 Minuten Audio",
        "val2": "Gleiche WER",
        "metric2": "Wortfehlerrate im selben Vergleichstest",
        "val3": "Idee → Produkt",
        "metric3": "Verantwortung gemeinsam mit dem CTO",
        "stack": "WEBRTC · SILERO VAD · RABBITMQ · FASTER-WHISPER · NVIDIA H100"
      },
      "project2": {
        "label": "NAROIQ — VERTEILTE SYSTEME / FINANCE — 2025",
        "title": "Marktdatenarchitektur mit ausfallsicherem Scheduling",
        "p1": "Bei NaroIQ verantwortete ich die Gesamtarchitektur einer verteilten Marktdatenplattform mit. Den Raft-basierten Scheduler für die ETF-Kalkulation entwarf und implementierte ich eigenständig. Er löste die unflexible Cron-Verarbeitung ab und koordinierte, welche Instanz die Verarbeitung über Events anstieß.",
        "p2": "Die Koordination mehrerer Scheduler-Instanzen diente der Ausfallsicherheit: Bei Ausfall der aktiven Instanz konnte eine andere die Event-Auslösung übernehmen. Mit dem Head of Engineering konzipierte ich RabbitMQ-Topic-Routing, getrennte Queues und spezialisierte Worker. Wiederanlauf und Retries stimmten wir im Team ab.",
        "p3": "Ich dokumentierte die Abläufe mit UML, baute AWS-Infrastruktur mit Terraform auf und stellte VPN-isolierte Dienste bereit. Grafana-Dashboards machten Verarbeitungsstatus und Kosten je Finanzinstrument nachvollziehbar. Außerdem administrierte ich Keycloak und band die Authentifizierung in die Dienste ein.",
        "alt": "NaroIQ-Scheduler-Dashboard mit Datenfeed-Verarbeitung und Infrastrukturstatus.",
        "val1": "Failover",
        "metric1": "Event-Auslösung bei Ausfall der aktiven Scheduler-Instanz übernehmen",
        "val2": "Gesamtarchitektur",
        "metric2": "Mitverantwortung für die verteilte Marktdatenplattform",
        "val3": "Kostentransparenz",
        "metric3": "Verarbeitungskosten je Finanzinstrument nachvollziehen",
        "stack": "RAFT · RABBITMQ · UML · AWS · TERRAFORM · GRAFANA · KEYCLOAK"
      },
      "project3": {
        "label": "HOLZ DISSELNMEYER — SYSTEMINTEGRATION & PRODUKTVERANTWORTUNG — 2023–HEUTE",
        "title": "Geschäftsregeln in ein durchgängiges System übersetzen",
        "p1": "Für Holz Disselnmeyer verantworte ich Architektur, Implementierung und Betrieb einer Plattform aus Shopware 6, Business Central und eigenem PIM. Mit Geschäftsführung, Fachbereichen und externen Zulieferern kläre ich Anforderungen und übersetze Preis-, Versand- und Auftragsabläufe in Systemgrenzen, Datenmodelle und Schnittstellen.",
        "p2": "Lokale Preismodelle und inkrementeller ERP-Abgleich vermeiden eine direkte ERP-Abhängigkeit bei jeder Preisabfrage. Bestellungen werden asynchron mit Retries und gespeichertem Verarbeitungsstatus übertragen. Versandregeln berücksichtigen unterschiedliche Einheiten, Maße und Lieferoptionen.",
        "p3": "Das PIM entwickelte ich mit FastAPI und React für über 15.000 Artikel einschließlich Varianten. Massenbearbeitung, KI-gestützte Gruppierung und Bildverarbeitung unterstützen die Katalogpflege. Die Oberfläche stimmte ich auf das Content- und SEO-Team ab. Tests sichern Releases und Migrationen ab; Grafana und Prometheus unterstützen Monitoring und Fehleranalyse.",
        "alt": "Commerce-Oberfläche mit Katalog, Preisen und Auftragsverwaltung.",
        "val1": "15.000+",
        "metric1": "Artikel einschließlich Varianten im PIM",
        "val2": "Gesamtverantwortung",
        "metric2": "Anforderungen, Architektur, Implementierung und Betrieb",
        "val3": "Seit 2025",
        "metric3": "Shop im Kundenbetrieb",
        "stack": "FASTAPI · REACT · BUSINESS CENTRAL · SHOPWARE 6 · REDIS · TERRAFORM"
      },
      "expand": {
        "readMore": "Projektdetails lesen",
        "showLess": "Projektdetails einklappen"
      }
    },
    "capabilities": {
      "label": "Expertise",
      "title": "Fachliche Anforderungen klären. Technische Entscheidungen fundieren.",
      "subtitle": "Ich erarbeite Lösungen im Austausch mit Fachseite und Entwicklungsteam, mache Vor- und Nachteile sichtbar und dokumentiere die Architektur mit UML und arc42. Die Umsetzung begleite ich mit Tests, Monitoring und Analyse im Produktivbetrieb.",
      "nodes": {
        "arch": {
          "name": "Anforderungsanalyse & Architektur",
          "detail": "Geschäftsregeln abstrahieren; Systemgrenzen und Lösungsalternativen erarbeiten"
        },
        "python": {
          "name": "Backend & Microservices",
          "detail": "Kritische Komponenten mit Python und Java; APIs mit klaren Verträgen"
        },
        "grafana": {
          "name": "Monitoring & Fehleranalyse",
          "detail": "Serviceübergreifende Ursachenanalyse; Verarbeitung und Kosten nachvollziehen"
        },
        "rust": {
          "name": "Systemintegration",
          "detail": "Bestehende Anwendungen verbinden; asynchrone Abläufe und Fehlerpfade gestalten"
        },
        "infra": {
          "name": "Cloud & Infrastruktur",
          "detail": "AWS, Terraform und Kubernetes; Bereitstellung und Betrieb begleiten"
        },
        "data": {
          "name": "Domänen- & Datenmodellierung",
          "detail": "Fachliche Regeln in Datenmodelle und nachvollziehbare Zustände übersetzen"
        },
        "middleware": {
          "name": "Verteilte Systeme",
          "detail": "Ereignisgesteuerte Verarbeitung, Ausfallsicherheit und Wiederanlauf"
        },
        "ai": {
          "name": "KI-Integration & Evaluation",
          "detail": "RAG, lokale Modelle und MCP; Qualität mit Evaluationssets prüfen"
        },
        "auth": {
          "name": "Sicherheit & Mandantentrennung",
          "detail": "Mandantentrennung konzipieren; Authentifizierung und mTLS integrieren"
        }
      }
    },
    "downloads": {
      "label": "Lebenslauf & Masterarbeit",
      "cv": {
        "title": "Lebenslauf",
        "meta": "PDF",
        "desc": "Projektverantwortung und berufliche Stationen: Architektur, Entwicklung und Betrieb mit Erfahrung in Finance und Insurance.",
        "cta": "Lebenslauf herunterladen",
        "file": "cv-de.pdf",
        "filename": "Aron_Gruenwald_Lebenslauf_DE.pdf"
      },
      "thesis": {
        "title": "Masterarbeit — Elastizität und Resilienz in Kubernetes",
        "meta": "PDF",
        "desc": "Wissenschaftliche Untersuchung zur Ressourcenauslastung in Kubernetes mit Helm, ArgoCD und Istio.",
        "cta": "Kubernetes-Masterarbeit lesen",
        "file": "master-thesis.pdf",
        "filename": "master-thesis.pdf"
      }
    },
    "contact": {
      "label": "Welche Aufgabe soll Ihr System als Nächstes lösen?",
      "intro": "Sie entwickeln eine bestehende Plattform weiter, klären Architekturentscheidungen oder integrieren KI-Funktionen? Schreiben Sie mir kurz, worum es geht. Ziele, Ausgangslage und Zusammenarbeit besprechen wir persönlich.",
      "emailLabel": "Projekt anfragen",
      "terminalTitle": "aron@aron-systems ~ portfolio",
      "terminalHint": "help, whoami, stack, projects oder contact eingeben.",
      "terminalPlaceholder": "Befehl eingeben (z. B. help)",
      "terminalAria": "Portfolio-Befehl",
      "terminalAriaLabel": "Portfolio-Terminal",
      "status": "Freiberufliche Zusammenarbeit · Umfang und Verfügbarkeit im Gespräch",
      "commands": {
        "help": [
          "Portfolio-Befehle:",
          "  help      -> Befehlsübersicht",
          "  whoami    -> Rolle und Verantwortung",
          "  stack     -> Technologien aus Projekten",
          "  projects  -> ausgewählte Projekte",
          "  uptime    -> Beruflicher Zeitraum",
          "  contact   -> E-Mail und Profile",
          "  clear     -> Ausgabe leeren"
        ],
        "whoami": [
          "Aron Grünwald",
          "Freiberuflicher Softwarearchitekt & Engineer.",
          "Anforderungsanalyse, Systemarchitektur und eigene Umsetzung.",
          "Erfahrung in Finance und Insurance sowie in verteilten Systemen und KI-Integration.",
          "Zusammenarbeit direkt mit Fachseite, CTOs und Entwicklungsteams."
        ],
        "stack": [
          "entwicklung:     Python, Java, TypeScript, PHP, FastAPI, Quarkus, React",
          "infrastruktur:   AWS, Kubernetes, Terraform, Docker",
          "messaging/daten: RabbitMQ, PostgreSQL, MySQL, Redis",
          "ki:              RAG, lokale Modelle, Evaluationssets, MCP, faster-whisper, vLLM",
          "integration:     OpenAPI, OData, Business Central, Shopware 6",
          "architektur:     UML, arc42, Domänenmodelle, ereignisgesteuerte Systeme",
          "qualität:        Unit-, Integrations- und Smoke-Tests; Grafana, Prometheus, ELK"
        ],
        "projects": [
          "Flexperto: Speech-to-Text von der Idee bis zum Produkt; Plattformarchitektur.",
          "NaroIQ: Marktdatenarchitektur mitverantwortet; ausfallsicheren Scheduler entwickelt.",
          "Holz Disselnmeyer: Anforderungen, Architektur und Betrieb; PIM für über 15.000 Artikel."
        ],
        "uptime": [
          "Softwareentwicklung seit Juni 2017.",
          "Von der Anforderungsanalyse über Architektur und Implementierung bis zum Betrieb."
        ],
        "contact": [
          "e-mail:    kontakt@aron-systems.de",
          "github:    github.com/knusperkrone",
          "gitlab:    gitlab.com/knukro",
          "linkedin:  linkedin.com/in/Aron-m"
        ]
      }
    },
    "footer": {
      "text": "© 2026 Aron Grünwald — Freiberuflicher Softwarearchitekt, Berlin"
    }
  }
};

let currentLang = 'en';

function getNestedTranslation(obj, path) {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((prev, curr) => (prev ? prev[curr] : undefined), obj);
}

function getTerminalScript(lang) {
  const isDe = lang === 'de';
  return [
    { type: 'command', text: 'contact' },
    { type: 'response', text: isDe ? 'Kontaktdaten' : 'Contact details', class: 'accent' },
    { type: 'response', text: `${isDe ? 'e-mail' : 'email'}: ${CONTENT.email}` },
    { type: 'response', text: isDe ? 'anfragen: rollen und projekte' : 'enquiries: roles and projects' },
    { type: 'blank' },

    { type: 'command', text: 'whoami' },
    { type: 'response', text: 'Aron Grünwald' },
    { type: 'response', text: isDe ? 'Softwarearchitekt / Senior Full-Stack Engineer' : 'Software Architect / Senior Full-Stack Engineer' },
    { type: 'response', text: isDe ? 'bereiche: finance / versicherungen / commerce' : 'domains: finance / insurance / commerce' },
    { type: 'blank' },

    { type: 'command', text: 'stack' },
    { type: 'response', text: isDe ? 'architektur:    [domänenmodelle, event-verarbeitung]' : 'architecture:    [domain models, event-driven processing]' },
    { type: 'response', text: isDe ? 'ki:              [batch-transkription, lokale modelle, transcript-retrieval]' : 'ai:              [batched transcription, local models, transcript retrieval]' },
    { type: 'response', text: 'erp:             [OData, Business Central, Shopware 6]' },
    { type: 'response', text: isDe ? 'verarbeitung:    [Raft-Scheduler, RabbitMQ-Worker, Redis-Jobs]' : 'processing:      [Raft scheduler, RabbitMQ workers, Redis jobs]' },
    { type: 'response', text: isDe ? 'infrastruktur:   [Kubernetes, Terraform, Grafana]' : 'infrastructure:  [Kubernetes, Terraform, Grafana]' },
    { type: 'blank' },

    { type: 'command', text: 'uptime' },
    { type: 'response', text: isDe ? 'Berufliche Softwareentwicklung seit Juni 2017' : 'Professional engineering since June 2017' },
  ];
}

function getTerminalCommands(lang) {
  const t = TRANSLATIONS[lang] || TRANSLATIONS.en;
  const cmds = { ...t.contact.commands };
  if (lang === 'de') {
    cmds['hilfe'] = cmds['help'];
    cmds['uebermich'] = cmds['whoami'];
    cmds['werbinich'] = cmds['whoami'];
    cmds['technologien'] = cmds['stack'];
    cmds['projekte'] = cmds['projects'];
    cmds['erfahrung'] = cmds['uptime'];
    cmds['kontakt'] = cmds['contact'];
    cmds['leeren'] = cmds['clear'];
  }
  return cmds;
}

// ============================================================
// ALL CODE BELOW IS UNCHANGED FROM ORIGINAL
// ============================================================

const CONFIG = {
  portraitCols: window.innerWidth < 768 ? 50 : 100,
  portraitRows: window.innerWidth < 768 ? 56 : 80,
  charWidth: 9,
  charHeight: 15,
  fontSize: 12,
  fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace",
  accent: '#C9A87C',
  accentDim: '#9a8270',
  text: '#e8e8e8',
  muted: '#767676',
  mouseRadius: 120,
  mouseForce: 16,
  spring: 0.1,
  damping: 0.84,
  velocityThreshold: 0.015,
  reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches
};

const canvas = document.getElementById('asciiCanvas');
const ctx = canvas.getContext('2d');
const asciiStatic = document.getElementById('asciiStatic');
const terminalOutput = document.getElementById('terminalOutput');
const terminalInput = document.getElementById('terminalInput');
const terminalHint = document.getElementById('terminalHint');
const siteHeader = document.getElementById('siteHeader');

function setLanguage(lang) {
  if (!TRANSLATIONS[lang]) return;
  currentLang = lang;
  try {
    localStorage.setItem('preferred_lang', lang);
  } catch (e) {}
  document.documentElement.lang = lang;

  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('lang') !== lang) {
      url.searchParams.set('lang', lang);
      window.history.replaceState({}, '', url.toString());
    }
  } catch (e) {}

  const t = TRANSLATIONS[lang];

  // 1. Meta tags and document title
  if (t.meta) {
    if (t.meta.title) document.title = t.meta.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && t.meta.description) {
      metaDesc.setAttribute('content', t.meta.description);
    }
  }

  // 2. Elements with text content: [data-i18n]
  document.querySelectorAll('[data-i18n]').forEach((el) => {
    const key = el.getAttribute('data-i18n');
    const val = getNestedTranslation(t, key);
    if (val !== undefined) {
      el.textContent = val;
    }
  });

  // 3. Elements with HTML formatting: [data-i18n-html]
  document.querySelectorAll('[data-i18n-html]').forEach((el) => {
    const key = el.getAttribute('data-i18n-html');
    const val = getNestedTranslation(t, key);
    if (val !== undefined) {
      el.innerHTML = val;
    }
  });

  // 4. Attribute mappings: [data-i18n-attr="attr1:key1,attr2:key2"]
  document.querySelectorAll('[data-i18n-attr]').forEach((el) => {
    const mappings = el.getAttribute('data-i18n-attr').split(',');
    mappings.forEach((mapping) => {
      const parts = mapping.split(':');
      if (parts.length === 2) {
        const attr = parts[0].trim();
        const key = parts[1].trim();
        const val = getNestedTranslation(t, key);
        if (val !== undefined) {
          el.setAttribute(attr, val);
        }
      }
    });
  });

  // 5. Hero CTA button
  const heroCta = document.getElementById('heroCta');
  if (heroCta && t.hero) {
    heroCta.textContent = t.hero.cta;
    heroCta.href = `mailto:${CONTENT.email}?subject=${encodeURIComponent(t.hero.ctaSubject)}`;
  }

  // 6. Downloads links
  const cvLink = document.getElementById('cvDownloadLink');
  if (cvLink && t.downloads && t.downloads.cv) {
    cvLink.setAttribute('href', t.downloads.cv.file);
    cvLink.setAttribute('download', t.downloads.cv.filename);
  }
  const thesisLink = document.getElementById('thesisDownloadLink');
  if (thesisLink && t.downloads && t.downloads.thesis) {
    thesisLink.setAttribute('href', t.downloads.thesis.file);
    thesisLink.setAttribute('download', t.downloads.thesis.filename);
  }

  // 7. Project expand buttons
  document.querySelectorAll('.project__expand').forEach((btn) => {
    const isExpanded = btn.getAttribute('aria-expanded') === 'true';
    btn.textContent = isExpanded ? t.work.expand.showLess : t.work.expand.readMore;
  });

  // 8. Terminal placeholder and hint
  if (terminalInput && t.contact) {
    terminalInput.placeholder = t.contact.terminalPlaceholder;
    terminalInput.setAttribute('aria-label', t.contact.terminalAria);
  }
  if (terminalHint && t.contact) {
    terminalHint.innerHTML = `<span aria-hidden="true">&rsaquo;</span> ${t.contact.terminalHint}`;
  }

  // 9. Language switcher buttons (sync both hero and header)
  document.querySelectorAll('.lang-btn').forEach((btn) => {
    const isActive = btn.dataset.lang === lang;
    btn.classList.toggle('is-active', isActive);
    btn.setAttribute('aria-pressed', String(isActive));
  });

  // 10. Update Constellation graph labels
  if (window.__updateArchConstellationLang) {
    window.__updateArchConstellationLang(lang);
  }
}

function initI18n() {
  const params = new URLSearchParams(window.location.search);
  const urlLang = params.get('lang');
  let storedLang = null;
  try {
    storedLang = localStorage.getItem('preferred_lang');
  } catch (e) {}
  const browserLang = navigator.language && navigator.language.toLowerCase().startsWith('de') ? 'de' : 'en';

  const initialLang = (urlLang && TRANSLATIONS[urlLang])
    ? urlLang
    : (storedLang && TRANSLATIONS[storedLang])
      ? storedLang
      : browserLang;

  document.querySelectorAll('.lang-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      setLanguage(btn.dataset.lang);
    });
  });

  setLanguage(initialLang in TRANSLATIONS ? initialLang : 'en');
}

let particles = [];
let mouse = { x: -9999, y: -9999 };
let isHovering = false;
let rafId = null;
let typingComplete = false;
let headerVisible = false;
let sourceCanvasCache = null;
let currentSrcW = 0;
let currentSrcH = 0;
let isTouch = false;
let resizeTimeout = null;
let pointerSetUp = false;

function hexToRgb(hex) {
  const clean = hex.replace('#', '');
  const bigint = parseInt(clean, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
}

function lerpColor(a, b, t) {
  const clamped = Math.max(0, Math.min(1, t));
  return [
    Math.round(a[0] + (b[0] - a[0]) * clamped),
    Math.round(a[1] + (b[1] - a[1]) * clamped),
    Math.round(a[2] + (b[2] - a[2]) * clamped)
  ];
}

function generatePortraitImage(width, height) {
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  const g = c.getContext('2d');
  g.fillStyle = '#080808';
  g.fillRect(0, 0, width, height);

  const cx = width / 2;
  const cy = height / 2;
  const rx = width * 0.26;
  const ry = height * 0.36;

  const grad = g.createRadialGradient(cx, cy - ry * 0.15, rx * 0.15, cx, cy, ry);
  grad.addColorStop(0, '#e8ddc5');
  grad.addColorStop(0.45, '#c4b595');
  grad.addColorStop(1, '#8c7c62');

  g.fillStyle = grad;
  g.beginPath();
  g.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  g.fill();

  return c;
}

async function loadPhotoOrFallback() {
  if (sourceCanvasCache) return sourceCanvasCache;
  if (!CONTENT.photoPath) {
    sourceCanvasCache = generatePortraitImage(800, 533);
    return sourceCanvasCache;
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = 800;
      c.height = 533;
      const g = c.getContext('2d');
      g.drawImage(img, 0, 0, 800, 533);
      sourceCanvasCache = c;
      resolve(c);
    };
    img.onerror = () => {
      sourceCanvasCache = generatePortraitImage(800, 533);
      resolve(sourceCanvasCache);
    };
    img.src = CONTENT.photoPath;
  });
}

function imageToParticles(sourceCanvas) {
  const srcW = CONFIG.portraitCols;
  const srcH = Math.floor(CONFIG.portraitRows * (CONFIG.charWidth / CONFIG.charHeight));

  const contrastCanvas = document.createElement('canvas');
  contrastCanvas.width = srcW;
  contrastCanvas.height = srcH;
  const cg = contrastCanvas.getContext('2d');
  cg.filter = 'contrast(1.35) brightness(1.12)';
  cg.drawImage(sourceCanvas, 0, 0, srcW, srcH);

  const imgData = cg.getImageData(0, 0, srcW, srcH);
  const pixels = imgData.data;
  const total = srcW * srcH * 4;
  const d = new Float32Array(total);
  for (let i = 0; i < total; i++) d[i] = pixels[i];

  const chars = '@%#*+=-:. ';
  const charSet = chars.split('');
  const result = [];

  for (let y = 0; y < srcH; y++) {
    for (let x = 0; x < srcW; x++) {
      const idx = (y * srcW + x) * 4;
      const r = d[idx], g = d[idx + 1], b = d[idx + 2];
      let brightness = 0.299 * r + 0.587 * g + 0.114 * b;

      brightness = ((brightness - 120) * 1.45) + 130;
      brightness = Math.max(0, Math.min(255, brightness));

      const charIndex = Math.floor((brightness / 255) * (charSet.length - 1));
      const char = charSet[charIndex];

      const targetBrightness = (charIndex / (charSet.length - 1)) * 255;
      const err = (brightness - targetBrightness) / 8;

      const offsets = [
        [1, 0], [2, 0],
        [-1, 1], [0, 1], [1, 1],
        [0, 2]
      ];
      for (const [ox, oy] of offsets) {
        const nx = x + ox, ny = y + oy;
        if (nx >= 0 && nx < srcW && ny < srcH) {
          const nidx = (ny * srcW + nx) * 4;
          d[nidx] = Math.max(0, Math.min(255, d[nidx] + err));
          d[nidx + 1] = Math.max(0, Math.min(255, d[nidx + 1] + err));
          d[nidx + 2] = Math.max(0, Math.min(255, d[nidx + 2] + err));
        }
      }

      const pr = pixels[idx], pg = pixels[idx + 1], pb = pixels[idx + 2];
      const px = x * CONFIG.charWidth;
      const py = y * CONFIG.charHeight;

      result.push({
        x: px, y: py,
        homeX: px, homeY: py,
        gridX: x, gridY: y,
        char,
        r: pr, g: pg, b: pb,
        vx: 0, vy: 0,
        alpha: 1
      });
    }
  }

  return { particles: result, srcW, srcH };
}

function buildStaticAscii(converted) {
  const lines = [];
  const srcW = converted.srcW;
  const srcH = converted.srcH;
  const { particles } = converted;
  for (let y = 0; y < srcH; y++) {
    let line = '';
    for (let x = 0; x < srcW; x++) {
      const p = particles[y * srcW + x];
      line += p.char === ' ' ? '\u00A0' : p.char;
    }
    lines.push(line);
  }
  asciiStatic.textContent = lines.join('\n');
}

function resizeCanvas() {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const rect = canvas.parentElement.getBoundingClientRect();
  canvas.width = Math.floor(rect.width * dpr);
  canvas.height = Math.floor(rect.height * dpr);
  canvas.style.width = rect.width + 'px';
  canvas.style.height = rect.height + 'px';
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.font = `${CONFIG.fontSize}px ${CONFIG.fontFamily}`;
  ctx.textBaseline = 'top';
}

function centerParticles(srcW, srcH, immediate = false) {
  const rect = canvas.parentElement.getBoundingClientRect();
  const boundsW = srcW * CONFIG.charWidth;
  const boundsH = srcH * CONFIG.charHeight;
  const offsetX = (rect.width - boundsW) / 2;
  const offsetY = (rect.height - boundsH) / 2;

  particles.forEach(p => {
    p.homeX = p.gridX * CONFIG.charWidth + offsetX;
    p.homeY = p.gridY * CONFIG.charHeight + offsetY;
    if (immediate) {
      p.x = p.homeX;
      p.y = p.homeY;
    }
  });
}

function setupPointer() {
  if (pointerSetUp) return;
  pointerSetUp = true;

  const hero = document.getElementById('hero');
  if (!hero) return;

  const hasHover = window.matchMedia('(hover: hover)').matches;

  let ticking = false;
  const clientMouse = { x: -9999, y: -9999 };

  const recalcMouseFromClient = () => {
    const rect = canvas.getBoundingClientRect();
    mouse.x = clientMouse.x - rect.left;
    mouse.y = clientMouse.y - rect.top;
  };

  const updateMouse = (clientX, clientY) => {
    clientMouse.x = clientX;
    clientMouse.y = clientY;
    recalcMouseFromClient();
    isHovering = true;
  };

  const restartOnPointer = () => startRenderLoop();

  if (hasHover) {
    hero.addEventListener('mousemove', (e) => {
      startRenderLoop();
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateMouse(e.clientX, e.clientY);
        ticking = false;
      });
    }, { passive: true });

    hero.addEventListener('mouseleave', () => {
      isHovering = false;
      clientMouse.x = -9999;
      clientMouse.y = -9999;
      mouse.x = -9999;
      mouse.y = -9999;
    }, { passive: true });

    window.addEventListener('scroll', () => {
      if (!isHovering) return;
      recalcMouseFromClient();
    }, { passive: true });
  }

  hero.addEventListener('touchstart', (e) => {
    restartOnPointer();
    if (e.touches.length) updateMouse(e.touches[0].clientX, e.touches[0].clientY);
  }, { passive: true });

  hero.addEventListener('touchmove', (e) => {
    restartOnPointer();
    if (e.touches.length) updateMouse(e.touches[0].clientX, e.touches[0].clientY);
  }, { passive: true });

  hero.addEventListener('touchend', () => {
    isHovering = false;
    mouse.x = -9999;
    mouse.y = -9999;
  }, { passive: true });
}

function startRenderLoop() {
  if (rafId) return;
  let lastTime = performance.now();
  let settledFrames = 0;

  function frame(now) {
    const dt = Math.min((now - lastTime) / 16.67, 2);
    lastTime = now;
    const maxSpeed = updateParticles(dt);
    render();

    if (maxSpeed < CONFIG.velocityThreshold && !isHovering) {
      settledFrames++;
      if (settledFrames > 5) {
        rafId = null;
        return;
      }
    } else {
      settledFrames = 0;
    }

    rafId = requestAnimationFrame(frame);
  }

  rafId = requestAnimationFrame(frame);
}

function stopRenderLoop() {
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
}

function updateParticles(dt) {
  let maxSpeed = 0;

  particles.forEach(p => {
    let tx = p.homeX;
    let ty = p.homeY;

    p.vx += (tx - p.x) * CONFIG.spring;
    p.vy += (ty - p.y) * CONFIG.spring;

    if (isHovering) {
      const dx = p.x - mouse.x;
      const dy = p.y - mouse.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < CONFIG.mouseRadius && dist > 0.5) {
        const force = (CONFIG.mouseRadius - dist) / CONFIG.mouseRadius;
        const angle = Math.atan2(dy, dx);
        p.vx += Math.cos(angle) * force * CONFIG.mouseForce;
        p.vy += Math.sin(angle) * force * CONFIG.mouseForce;
      }
    }

    p.vx *= CONFIG.damping;
    p.vy *= CONFIG.damping;

    p.x += p.vx * dt;
    p.y += p.vy * dt;

    const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
    if (speed > maxSpeed) maxSpeed = speed;
  });

  return maxSpeed;
}

function render() {
  const rect = canvas.parentElement.getBoundingClientRect();
  ctx.clearRect(0, 0, rect.width, rect.height);

  const accent = hexToRgb(CONFIG.accent);
  const dim = hexToRgb(CONFIG.accentDim);

  particles.forEach(p => {
    const lum = 0.299 * p.r + 0.587 * p.g + 0.114 * p.b;
    const brightness = lum / 255;
    const tone = lerpColor(dim, accent, brightness * 0.85 + 0.15);

    ctx.fillStyle = `rgb(${tone[0]}, ${tone[1]}, ${tone[2]})`;
    ctx.globalAlpha = 0.6 + brightness * 0.4;
    ctx.fillText(p.char, p.x, p.y);
  });

  ctx.globalAlpha = 1;
}

function getAsciiDensity() {
  return {
    cols: window.innerWidth < 768 ? 50 : 100,
    rows: window.innerWidth < 768 ? 56 : 80
  };
}

function rebuildAscii() {
  const density = getAsciiDensity();
  const densityChanged = CONFIG.portraitCols !== density.cols || CONFIG.portraitRows !== density.rows;

  if (densityChanged) {
    CONFIG.portraitCols = density.cols;
    CONFIG.portraitRows = density.rows;
  }

  if (CONFIG.reducedMotion) {
    loadPhotoOrFallback().then(portraitCanvas => {
      const converted = imageToParticles(portraitCanvas);
      particles = converted.particles;
      currentSrcW = converted.srcW;
      currentSrcH = converted.srcH;
      buildStaticAscii(converted);
      asciiStatic.style.display = 'grid';
      canvas.style.display = 'none';
    });
    return;
  }

  loadPhotoOrFallback().then(portraitCanvas => {
    resizeCanvas();

    if (densityChanged || particles.length === 0) {
      const converted = imageToParticles(portraitCanvas);
      particles = converted.particles;
      currentSrcW = converted.srcW;
      currentSrcH = converted.srcH;
    }

    centerParticles(currentSrcW, currentSrcH, true);

    asciiStatic.style.display = 'none';
    canvas.style.display = 'block';
    setupPointer();
    startRenderLoop();
  });
}

function initAsciiPortrait() {
  isTouch = window.matchMedia('(pointer: coarse)').matches;
  const density = getAsciiDensity();
  CONFIG.portraitCols = density.cols;
  CONFIG.portraitRows = density.rows;

  rebuildAscii();

  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(rebuildAscii, 180);
  }, { passive: true });

  const hero = document.getElementById('hero');
  if (!hero || CONFIG.reducedMotion) return;

  const heroObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        startRenderLoop();
      } else {
        stopRenderLoop();
      }
    });
  }, { threshold: 0 });

  heroObserver.observe(hero);
}

function initObservers() {
  if (CONFIG.reducedMotion) {
    document.querySelectorAll('[data-animate]').forEach(el => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
      } else if (entry.boundingClientRect.y > 0) {
        entry.target.classList.remove('is-visible');
      }
    });
  }, { threshold: 0.2, rootMargin: '0px 0px -50px 0px' });

  document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));
}

function initViewportHeight() {
  let lastWidth = window.innerWidth;
  const set = () => {
    document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    lastWidth = window.innerWidth;
  };
  set();
  window.addEventListener('resize', () => {
    if (window.innerWidth !== lastWidth) set();
  }, { passive: true });
}

function initHeader() {
  const hero = document.getElementById('hero');
  if (!hero || !siteHeader) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        siteHeader.classList.remove('is-visible');
        siteHeader.setAttribute('aria-hidden', 'true');
        headerVisible = false;
      } else {
        siteHeader.classList.add('is-visible');
        siteHeader.setAttribute('aria-hidden', 'false');
        headerVisible = true;
      }
    });
  }, { threshold: 0.05 });

  observer.observe(hero);
}

function renderStaticTerminal() {
  terminalOutput.innerHTML = '';
  const script = getTerminalScript(currentLang);
  script.forEach((line) => {
    if (line.type === 'blank') {
      terminalOutput.appendChild(document.createElement('br'));
      return;
    }
    const span = document.createElement('span');
    span.className = `terminal__line terminal__line--${line.type} ${line.class ? `terminal__line--${line.class}` : ''}`;
    const prefix = line.type === 'command' ? '$ ' : '> ';
    span.textContent = prefix + line.text;
    terminalOutput.appendChild(span);
  });
  terminalOutput.scrollTop = terminalOutput.scrollHeight;
}

function typeTerminalSequence() {
  let index = 0;
  let skipTyping = false;
  terminalOutput.innerHTML = '';
  terminalInput.disabled = false;
  if (terminalHint) terminalHint.hidden = false;

  const script = getTerminalScript(currentLang);

  const skipSequence = () => {
    if (skipTyping || typingComplete) return;
    skipTyping = true;
    renderStaticTerminal();
    finishTyping();
  };

  terminalInput.addEventListener('focus', skipSequence, { once: true });
  terminalInput.addEventListener('keydown', skipSequence, { once: true });
  terminalInput.addEventListener('input', skipSequence, { once: true });

  function nextLine() {
    if (skipTyping) return;
    if (index >= script.length) {
      finishTyping();
      return;
    }

    const line = script[index++];

    if (line.type === 'blank') {
      terminalOutput.appendChild(document.createElement('br'));
      setTimeout(nextLine, 20);
      return;
    }

    const span = document.createElement('span');
    span.className = `terminal__line terminal__line--${line.type} ${line.class ? `terminal__line--${line.class}` : ''}`;
    const prefix = line.type === 'command' ? '$ ' : '> ';
    span.textContent = prefix;
    terminalOutput.appendChild(span);
    terminalOutput.scrollTop = terminalOutput.scrollHeight;

    const text = line.text;
    let charIndex = 0;
    const cursor = document.createElement('span');
    cursor.className = 'terminal__cursor';
    cursor.textContent = '▋';
    span.appendChild(cursor);

    function typeChar() {
      if (skipTyping) return;
      if (charIndex < text.length) {
        span.insertBefore(document.createTextNode(text[charIndex]), cursor);
        charIndex++;
        terminalOutput.scrollTop = terminalOutput.scrollHeight;
        setTimeout(typeChar, 2 + Math.random() * 3);
      } else {
        cursor.remove();
        setTimeout(nextLine, 20);
      }
    }

    typeChar();
  }

  nextLine();
}

function finishTyping() {
  typingComplete = true;
  terminalInput.disabled = false;
  const t = TRANSLATIONS[currentLang] || TRANSLATIONS.en;
  terminalInput.placeholder = t.contact.terminalPlaceholder;
  if (terminalHint) terminalHint.hidden = false;
}

function printLine(text, type) {
  const span = document.createElement('span');
  span.className = `terminal__line terminal__line--${type}`;
  span.textContent = text;
  terminalOutput.appendChild(span);
  terminalOutput.scrollTop = terminalOutput.scrollHeight;
}

function initTerminal() {
  if (!terminalInput || !terminalOutput) return;

  terminalInput.disabled = true;

  if (CONFIG.reducedMotion) {
    renderStaticTerminal();
    finishTyping();
  } else {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !typingComplete) {
          typeTerminalSequence();
          observer.disconnect();
        }
      });
    }, { threshold: 0.5 });

    observer.observe(document.getElementById('terminal'));
  }

  terminalInput.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const raw = terminalInput.value.trim();
    if (!raw) return;

    const cmd = raw.toLowerCase();
    printLine('$ ' + raw, 'command');

    const commands = getTerminalCommands(currentLang);
    if (cmd === 'clear' || cmd === 'leeren') {
      terminalOutput.innerHTML = '';
    } else if (commands[cmd]) {
      commands[cmd].forEach((line, i) => {
        setTimeout(() => printLine('> ' + line, 'response'), i * 60);
      });
    } else {
      const isDe = currentLang === 'de';
      printLine(`> ${isDe ? `Unbekannter Portfolio-Befehl: ${raw}` : `Unknown portfolio command: ${raw}`}`, 'error');
      printLine(`> ${isDe ? 'Für Portfolio-Befehle "help" oder "hilfe" eingeben.' : 'Enter "help" to see portfolio commands.'}`, 'response');
    }

    terminalInput.value = '';
  });
}

// ============================================================
// ARCHITECT CONSTELLATION — Capability topology
// ============================================================
(function initArchConstellation() {
  const DENSITY = '@%#*+=-:. ';
  const DENSITY_ARR = DENSITY.split('');
  const ACCENT = [201, 168, 124];
  const ACCENT_DIM = [154, 130, 112];
  const FONT = "'Geist Mono', 'IBM Plex Mono', monospace";

  function lerpTone(t) {
    const c = Math.max(0, Math.min(1, t));
    return [
      Math.round(ACCENT_DIM[0] + (ACCENT[0] - ACCENT_DIM[0]) * c),
      Math.round(ACCENT_DIM[1] + (ACCENT[1] - ACCENT_DIM[1]) * c),
      Math.round(ACCENT_DIM[2] + (ACCENT[2] - ACCENT_DIM[2]) * c)
    ];
  }

  function bezierPoint(t, p0, p1, p2) {
    const u = 1 - t;
    return {
      x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
      y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y
    };
  }

  const isTouch = window.matchMedia('(pointer: coarse)').matches;
  const isMobile = window.innerWidth < 768 || isTouch;

  const section = document.getElementById('capabilities');
  if (section) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('is-visible'); });
    }, { threshold: 0.1 });
    io.observe(section);
  }

  const canvas = document.getElementById('archCanvas');
  if (!canvas || isMobile) return;

  const ctx = canvas.getContext('2d');

  const tCap = (TRANSLATIONS[currentLang] || TRANSLATIONS.en).capabilities.nodes;
  const NODES = [
    {
      id: 'arch', icon: '@', label: tCap.arch ? tCap.arch.name : 'Software architecture', x: 0.50, y: 0.50, isCenter: true,
      conns: ['python', 'grafana', 'rust', 'infra', 'data', 'middleware', 'ai', 'auth']
    },
    { id: 'python', icon: '#', label: tCap.python ? tCap.python.name : 'Python services', x: 0.20, y: 0.72, conns: ['middleware', 'data', 'infra'] },
    { id: 'grafana', icon: '$', label: tCap.grafana ? tCap.grafana.name : 'Production diagnosis', x: 0.80, y: 0.72, conns: ['infra', 'data'] },
    { id: 'rust', icon: '*', label: tCap.rust ? tCap.rust.name : 'ERP integration', x: 0.25, y: 0.30, conns: ['infra', 'middleware'] },
    { id: 'infra', icon: '%', label: tCap.infra ? tCap.infra.name : 'Infrastructure as code', x: 0.75, y: 0.30, conns: ['ai', 'auth', 'python', 'grafana'] },
    { id: 'data', icon: '+', label: tCap.data ? tCap.data.name : 'Domain and data modelling', x: 0.12, y: 0.52, conns: ['python', 'grafana', 'middleware'] },
    { id: 'middleware', icon: '~', label: tCap.middleware ? tCap.middleware.name : 'Distributed processing', x: 0.30, y: 0.88, conns: ['python', 'data', 'rust'] },
    { id: 'ai', icon: '^', label: tCap.ai ? tCap.ai.name : 'AI product integration', x: 0.88, y: 0.48, conns: ['infra'] },
    { id: 'auth', icon: '=', label: tCap.auth ? tCap.auth.name : 'Service security', x: 0.72, y: 0.82, conns: ['infra', 'ai'] }
  ];

  window.__updateArchConstellationLang = function(lang) {
    const t = TRANSLATIONS[lang] || TRANSLATIONS.en;
    if (!t || !t.capabilities || !t.capabilities.nodes) return;
    NODES.forEach(n => {
      if (t.capabilities.nodes[n.id]) {
        n.label = t.capabilities.nodes[n.id].name;
      }
    });
    labels.forEach(l => {
      if (t.capabilities.nodes[l.nodeId]) {
        l.text = t.capabilities.nodes[l.nodeId].name;
      }
    });
  };

  let W, H;
  let particles = [];
  let flowParticles = [];
  let connections = [];
  let labels = [];
  let mouse = { x: -9999, y: -9999 };
  let hovered = null;
  let rafId = null, visible = false, time = 0;

  function resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const r = canvas.parentElement.getBoundingClientRect();
    canvas.width = r.width * dpr;
    canvas.height = r.height * dpr;
    canvas.style.width = r.width + 'px';
    canvas.style.height = r.height + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    W = r.width;
    H = r.height;
  }

  function build() {
    particles = [];
    flowParticles = [];
    connections = [];
    labels = [];

    for (let i = 0; i < 100; i++) {
      particles.push({
        x: Math.random() * W, y: Math.random() * H,
        homeX: Math.random() * W, homeY: Math.random() * H,
        vx: 0, vy: 0, char: ['·', '.', '-', ':'][Math.floor(Math.random() * 4)],
        brightness: 0.04 + Math.random() * 0.08, isIcon: false, isAmbient: true,
        nodeId: null, size: 0.35
      });
    }

    NODES.forEach(node => {
      const cx = node.x * W, cy = node.y * H;
      const isCenter = node.isCenter;

      labels.push({ x: cx, y: cy + (isCenter ? 34 : 26), text: node.label, nodeId: node.id, isCenter });

      particles.push({
        x: cx, y: cy, homeX: cx, homeY: cy, vx: 0, vy: 0,
        char: node.icon, brightness: isCenter ? 1.0 : 0.92,
        isIcon: true, isCenter, isAmbient: false,
        nodeId: node.id, size: isCenter ? 2.0 : 1.4
      });

      const clusterCount = isCenter ? 42 : 24;
      for (let i = 0; i < clusterCount; i++) {
        const a = (i / clusterCount) * Math.PI * 2 + (isCenter ? 0 : Math.random() * 0.4);
        const rad = isCenter ? (8 + Math.random() * 32) : (6 + Math.random() * 22);
        const px = cx + Math.cos(a) * rad;
        const py = cy + Math.sin(a) * rad * 0.5;
        const di = Math.floor(Math.random() * (isCenter ? 6 : 5)) + (isCenter ? 3 : 4);
        particles.push({
          x: px, y: py, homeX: px, homeY: py, vx: 0, vy: 0,
          char: DENSITY_ARR[Math.min(9, di)], brightness: di / 9,
          isIcon: false, isAmbient: false, nodeId: node.id,
          isCenterCluster: isCenter, size: isCenter ? 0.75 : 0.65
        });
      }

      if (isCenter) {
        for (let i = 0; i < 20; i++) {
          const a = (i / 20) * Math.PI * 2;
          const rad = 38 + Math.random() * 4;
          const px = cx + Math.cos(a) * rad;
          const py = cy + Math.sin(a) * rad * 0.4;
          particles.push({
            x: px, y: py, homeX: px, homeY: py, vx: 0, vy: 0,
            char: DENSITY_ARR[4 + Math.floor(Math.random() * 3)],
            brightness: 0.35, isIcon: false, isAmbient: false,
            nodeId: node.id, isGlowRing: true, size: 0.5
          });
        }
      }

      node.conns.forEach(tid => {
        const tgt = NODES.find(n => n.id === tid);
        if (!tgt) return;
        const tx = tgt.x * W, ty = tgt.y * H;

        const mx = (cx + tx) / 2, my = (cy + ty) / 2;
        const dx = tx - cx, dy = ty - cy;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const perpX = -dy / len * 8;
        const perpY = dx / len * 8;
        const cpX = mx + perpX;
        const cpY = my + perpY;

        connections.push({
          from: node.id, to: tgt.id,
          p0: { x: cx, y: cy }, p1: { x: cpX, y: cpY }, p2: { x: tx, y: ty },
          isFromCenter: node.isCenter
        });

        const steps = Math.max(4, Math.floor(len / 7));
        for (let s = 1; s < steps; s++) {
          const t = s / steps;
          const bp = bezierPoint(t, { x: cx, y: cy }, { x: cpX, y: cpY }, { x: tx, y: ty });
          const jitter = (Math.random() - 0.5) * 3;
          particles.push({
            x: bp.x + jitter, y: bp.y + jitter,
            homeX: bp.x + jitter, homeY: bp.y + jitter,
            vx: 0, vy: 0,
            char: s % 4 === 0 ? '·' : (s % 4 === 2 ? '-' : ' '),
            brightness: 0.12, isIcon: false, isAmbient: false,
            nodeId: null, fromNode: node.id, toNode: tid, size: 0.35
          });
        }
      });
    });
  }

  function spawnFlowParticle() {
    if (connections.length === 0) return;
    const fromCenter = Math.random() < 0.6;
    let conn;
    if (fromCenter) {
      const centerConns = connections.filter(c => c.isFromCenter);
      conn = centerConns[Math.floor(Math.random() * centerConns.length)];
    } else {
      conn = connections[Math.floor(Math.random() * connections.length)];
    }
    if (!conn) conn = connections[Math.floor(Math.random() * connections.length)];

    flowParticles.push({
      conn, progress: 0,
      speed: 0.004 + Math.random() * 0.006,
      char: fromCenter ? ['*', '+', '@'][Math.floor(Math.random() * 3)] : ['·', '-', ':'][Math.floor(Math.random() * 3)],
      brightness: fromCenter ? 0.8 : 0.5,
      isFromCenter: fromCenter
    });
  }

  function bindEvents() {
    canvas.addEventListener('mousemove', e => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
      let near = null, nearD = 50;
      NODES.forEach(n => {
        const d = Math.sqrt((mouse.x - n.x * W) ** 2 + (mouse.y - n.y * H) ** 2);
        if (d < nearD) { near = n.id; nearD = d; }
      });
      hovered = near;
    }, { passive: true });

    canvas.addEventListener('mouseleave', () => {
      mouse.x = -9999; mouse.y = -9999; hovered = null;
    });

    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        visible = e.isIntersecting;
        if (visible && !rafId) rafId = requestAnimationFrame(loop);
        else if (!visible && rafId) { cancelAnimationFrame(rafId); rafId = null; }
      });
    }, { threshold: 0 });
    obs.observe(canvas.closest('.arch-constellation__wrap'));

    document.querySelectorAll('.skill-card').forEach(card => {
      card.addEventListener('mouseenter', () => { hovered = card.dataset.skill; });
      card.addEventListener('mouseleave', () => { hovered = null; });
    });
  }

  function loop() {
    if (!visible) { rafId = null; return; }
    time += 0.003;
    render();
    rafId = requestAnimationFrame(loop);
  }

  function render() {
    ctx.clearRect(0, 0, W, H);

    const activeNodes = new Set();
    const activeConns = [];
    if (hovered) {
      activeNodes.add(hovered);
      const n = NODES.find(n => n.id === hovered);
      if (n) {
        n.conns.forEach(c => { const cn = NODES.find(x => x.id === c); if (cn) activeNodes.add(cn.id); });
        NODES.forEach(nn => { if (nn.conns.includes(hovered)) activeNodes.add(nn.id); });
        n.conns.forEach(c => activeConns.push([hovered, c]));
        NODES.forEach(nn => { if (nn.conns.includes(hovered)) activeConns.push([nn.id, hovered]); });
      }
    }

    particles.forEach(p => {
      const dx = p.isAmbient
        ? Math.sin(time * 0.5 + p.homeX * 0.04) * 0.7
        : Math.sin(time + p.homeX * 0.06) * 0.3;
      const dy = p.isAmbient
        ? Math.cos(time * 0.4 + p.homeY * 0.04) * 0.7
        : Math.cos(time * 0.8 + p.homeY * 0.06) * 0.3;

      const mdx = p.x - mouse.x, mdy = p.y - mouse.y;
      const md = Math.sqrt(mdx * mdx + mdy * mdy);
      let rx = 0, ry = 0;
      if (md < 55 && md > 0.5) {
        const f = (55 - md) / 55;
        rx = (mdx / md) * f * 3;
        ry = (mdy / md) * f * 3;
      }

      p.vx += (p.homeX + dx - p.x) * 0.035 + rx * 0.08;
      p.vy += (p.homeY + dy - p.y) * 0.035 + ry * 0.08;
      p.vx *= 0.94;
      p.vy *= 0.94;
      p.x += p.vx;
      p.y += p.vy;

      let isAct = false;
      if (hovered) {
        if (p.nodeId && activeNodes.has(p.nodeId)) isAct = true;
        if (p.fromNode && activeConns.some(c => c[0] === p.fromNode && c[1] === p.toNode)) isAct = true;
      }

      let b = p.brightness;
      if (isAct) b = Math.min(1, b * 2.2);
      else if (hovered && !p.isIcon && !p.isAmbient && !p.isGlowRing) b *= 0.07;

      if (p.isCenterCluster && !hovered) b = Math.min(1, b * 1.3);

      const tone = lerpTone(b * 0.85 + 0.15);
      ctx.fillStyle = `rgb(${tone[0]},${tone[1]},${tone[2]})`;
      ctx.globalAlpha = (p.isAmbient ? 0.2 : (p.isGlowRing ? 0.25 : 0.42 + b * 0.58)) * (isAct ? 1.25 : 1);
      if (p.char === ' ') ctx.globalAlpha = 0;

      const fs = p.isIcon ? (p.isCenter ? 28 : 22) : Math.max(9, 13 * p.size);
      ctx.font = `${fs}px ${FONT}`;
      const tw = ctx.measureText(p.char).width;
      ctx.fillText(p.char, p.x - tw / 2, p.y - (p.isIcon ? (p.isCenter ? 16 : 12) : 5));
    });

    if (Math.random() < 0.09) spawnFlowParticle();

    for (let i = flowParticles.length - 1; i >= 0; i--) {
      const fp = flowParticles[i];
      fp.progress += fp.speed;
      if (fp.progress >= 1) { flowParticles.splice(i, 1); continue; }

      const bp = bezierPoint(fp.progress, fp.conn.p0, fp.conn.p1, fp.conn.p2);

      let isActiveFlow = false;
      if (hovered) {
        if (fp.conn.from === hovered || fp.conn.to === hovered) isActiveFlow = true;
        if (activeConns.some(c => c[0] === fp.conn.from && c[1] === fp.conn.to)) isActiveFlow = true;
      }

      const tone = lerpTone(fp.brightness * 0.85 + 0.15);
      ctx.fillStyle = `rgb(${tone[0]},${tone[1]},${tone[2]})`;
      ctx.globalAlpha = isActiveFlow ? 0.95 : (hovered ? 0.15 : 0.55);

      const fs = isActiveFlow ? (fp.isFromCenter ? 13 : 11) : (fp.isFromCenter ? 10 : 8);
      ctx.font = `${fs}px ${FONT}`;
      const tw = ctx.measureText(fp.char).width;
      ctx.fillText(fp.char, bp.x - tw / 2, bp.y - 5);

      if (isActiveFlow || !hovered) {
        ctx.globalAlpha = isActiveFlow ? 0.2 : 0.05;
        ctx.fillStyle = fp.isFromCenter ? `rgb(${ACCENT[0]},${ACCENT[1]},${ACCENT[2]})` : `rgb(${ACCENT_DIM[0]},${ACCENT_DIM[1]},${ACCENT_DIM[2]})`;
        const trailT = Math.max(0, fp.progress - 0.05);
        const trailBp = bezierPoint(trailT, fp.conn.p0, fp.conn.p1, fp.conn.p2);
        ctx.font = `7px ${FONT}`;
        ctx.fillText('·', trailBp.x - 3, trailBp.y - 3);
      }
    }

    ctx.globalAlpha = 1;

    labels.forEach(l => {
      const isActive = !hovered || hovered === l.nodeId || activeNodes.has(l.nodeId);
      ctx.font = l.isCenter ? `bold 12px ${FONT}` : `11px ${FONT}`;
      const tw = ctx.measureText(l.text).width;

      if (hovered && hovered === l.nodeId) {
        ctx.fillStyle = `rgb(${ACCENT[0]},${ACCENT[1]},${ACCENT[2]})`;
        ctx.globalAlpha = 1;
        ctx.fillText(l.text, l.x - tw / 2, l.y);
        ctx.fillRect(l.x - tw / 2, l.y + 3, tw, 1);
      } else if (!hovered) {
        ctx.fillStyle = l.isCenter ? `rgb(${ACCENT[0]},${ACCENT[1]},${ACCENT[2]})` : `rgb(${ACCENT_DIM[0]},${ACCENT_DIM[1]},${ACCENT_DIM[2]})`;
        ctx.globalAlpha = l.isCenter ? 0.95 : 0.82;
        ctx.fillText(l.text, l.x - tw / 2, l.y);
      } else {
        ctx.fillStyle = l.isCenter ? `rgb(${ACCENT[0]},${ACCENT[1]},${ACCENT[2]})` : `rgb(${ACCENT_DIM[0]},${ACCENT_DIM[1]},${ACCENT_DIM[2]})`;
        ctx.globalAlpha = isActive ? (l.isCenter ? 0.9 : 0.5) : 0.08;
        ctx.fillText(l.text, l.x - tw / 2, l.y);
      }
    });
    ctx.globalAlpha = 1;

    if (flowParticles.length > 45) flowParticles.splice(0, flowParticles.length - 45);
  }

  resize();
  build();
  bindEvents();
  rafId = requestAnimationFrame(loop);
  window.addEventListener('resize', () => { resize(); build(); });
})();

function initProjectExpand() {
  const bodies = document.querySelectorAll('.project__body[data-expandable]');

  bodies.forEach(body => {
    const btn = body.nextElementSibling;
    if (!btn || !btn.classList.contains('project__expand')) return;

    function update() {
      const overflows = body.scrollHeight > Math.ceil(body.clientHeight) + 2;
      if (overflows) {
        btn.hidden = false;
      } else {
        body.classList.remove('is-collapsed');
        btn.hidden = true;
      }
    }

    update();
    window.addEventListener('resize', update, { passive: true });

    btn.addEventListener('click', () => {
      const willExpand = body.classList.contains('is-collapsed');
      body.classList.toggle('is-collapsed', !willExpand);
      body.classList.toggle('is-expanded', willExpand);
      btn.setAttribute('aria-expanded', String(willExpand));
      const t = TRANSLATIONS[currentLang] || TRANSLATIONS.en;
      btn.textContent = willExpand ? t.work.expand.showLess : t.work.expand.readMore;
    });
  });
}

function init() {
  initI18n();
  initViewportHeight();
  initAsciiPortrait();
  initObservers();
  initHeader();
  initTerminal();
  initProjectExpand();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
